import { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { 
  Send, 
  Loader2, 
  Bot, 
  User, 
  Package, 
  TrendingUp, 
  AlertTriangle,
  Search,
  Clock,
  DollarSign,
  MapPin,
  Sparkles
} from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { useStockItems } from "@/hooks/use-stock-items";

interface Message {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: Date;
  status?: "sending" | "sent" | "failed";
}

interface QuickAction {
  id: string;
  label: string;
  icon: React.ReactNode;
  query: string;
  category: string;
}

const quickActions: QuickAction[] = [
  {
    id: "low-stock",
    label: "Low Stock Items",
    icon: <AlertTriangle className="h-4 w-4" />,
    query: "Show me all items with low stock levels",
    category: "Stock Levels"
  },
  {
    id: "recent-movements",
    label: "Recent Movements",
    icon: <TrendingUp className="h-4 w-4" />,
    query: "What are the recent stock movements?",
    category: "Stock Movement"
  },
  {
    id: "high-value",
    label: "High Value Items",
    icon: <DollarSign className="h-4 w-4" />,
    query: "Show me the highest value items in stock",
    category: "Price Inquiry"
  },
  {
    id: "search-sku",
    label: "Search by SKU",
    icon: <Search className="h-4 w-4" />,
    query: "Search for stock item by code",
    category: "Product Info"
  },
  {
    id: "warehouse-stock",
    label: "Warehouse Overview",
    icon: <MapPin className="h-4 w-4" />,
    query: "Show stock distribution across warehouses",
    category: "Inventory Analytics"
  },
  {
    id: "stock-value",
    label: "Total Stock Value",
    icon: <Package className="h-4 w-4" />,
    query: "What is the total value of current inventory?",
    category: "Inventory Analytics"
  }
];

export function StockAssistantPage() {
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const { stockItems } = useStockItems();

  // Load conversation history from localStorage on mount
  useEffect(() => {
    const savedMessages = localStorage.getItem("stock-assistant-messages");
    if (savedMessages) {
      try {
        const parsed = JSON.parse(savedMessages);
        // Convert timestamp strings back to Date objects
        const messagesWithDates = parsed.map((msg: Message) => ({
          ...msg,
          timestamp: new Date(msg.timestamp)
        }));
        setMessages(messagesWithDates);
      } catch (error) {
        console.error("Failed to load conversation history:", error);
      }
    } else {
      // Add welcome message if no history
      const welcomeMessage: Message = {
        id: crypto.randomUUID(),
        role: "assistant",
        content: "Hello! I'm your Stock Management Assistant. I can help you with:\n\n• Stock level inquiries and availability checks\n• Recent inventory movements and transfers\n• Price information and cost analysis\n• Product details and SKU lookups\n• Warehouse locations and distribution\n• Inventory analytics and reports\n\nHow can I assist you today?",
        timestamp: new Date(),
        status: "sent"
      };
      setMessages([welcomeMessage]);
    }
  }, []);

  // Save messages to localStorage whenever they change
  useEffect(() => {
    if (messages.length > 0) {
      localStorage.setItem("stock-assistant-messages", JSON.stringify(messages));
    }
  }, [messages]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (scrollAreaRef.current) {
      const scrollContainer = scrollAreaRef.current.querySelector('[data-radix-scroll-area-viewport]');
      if (scrollContainer) {
        scrollContainer.scrollTop = scrollContainer.scrollHeight;
      }
    }
  }, [messages]);

  const processQuery = (query: string): string => {
    const lowerQuery = query.toLowerCase();

    // Low stock detection
    if (lowerQuery.includes("low stock") || lowerQuery.includes("minimum stock")) {
      const lowStockItems = stockItems.filter(item => 
        !item.inactive && item.physicalQuantity < 50
      );
      
      if (lowStockItems.length === 0) {
        return "Great news! All active items are above minimum stock levels.";
      }

      let response = `I found ${lowStockItems.length} items with low stock levels:\n\n`;
      lowStockItems.slice(0, 5).forEach(item => {
        response += `• **${item.code}** - ${item.description}\n  Current: ${item.physicalQuantity} units | Price: $${item.unitPrice.toFixed(2)}\n\n`;
      });

      if (lowStockItems.length > 5) {
        response += `...and ${lowStockItems.length - 5} more items.\n\n`;
      }

      response += "💡 **Suggestion:** Consider creating purchase orders for these items.";
      return response;
    }

    // Stock value calculation
    if (lowerQuery.includes("total") && (lowerQuery.includes("value") || lowerQuery.includes("worth"))) {
      const totalValue = stockItems
        .filter(item => !item.inactive)
        .reduce((sum, item) => sum + (item.physicalQuantity * item.unitPrice), 0);
      
      const itemCount = stockItems.filter(item => !item.inactive).length;
      const totalUnits = stockItems
        .filter(item => !item.inactive)
        .reduce((sum, item) => sum + item.physicalQuantity, 0);

      return `📊 **Current Inventory Summary:**\n\n• **Total Value:** $${totalValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}\n• **Active Items:** ${itemCount} products\n• **Total Units:** ${totalUnits.toLocaleString()} units\n\n💡 This represents your current stock on hand across all warehouses.`;
    }

    // High value items
    if (lowerQuery.includes("high value") || lowerQuery.includes("highest value") || lowerQuery.includes("expensive")) {
      const sortedByValue = [...stockItems]
        .filter(item => !item.inactive)
        .sort((a, b) => (b.physicalQuantity * b.unitPrice) - (a.physicalQuantity * a.unitPrice));

      let response = "💰 **Top 5 Highest Value Items:**\n\n";
      sortedByValue.slice(0, 5).forEach((item, index) => {
        const totalValue = item.physicalQuantity * item.unitPrice;
        response += `${index + 1}. **${item.code}** - ${item.description}\n   Qty: ${item.physicalQuantity} × $${item.unitPrice.toFixed(2)} = $${totalValue.toFixed(2)}\n\n`;
      });

      return response;
    }

    // Warehouse distribution
    if (lowerQuery.includes("warehouse") && (lowerQuery.includes("distribution") || lowerQuery.includes("overview"))) {
      const warehouseStats = stockItems
        .filter(item => !item.inactive)
        .reduce((acc, item) => {
          const locations = item.locationQuantities || [];
          locations.forEach(loc => {
            if (!acc[loc.warehouseName]) {
              acc[loc.warehouseName] = { items: 0, quantity: 0, value: 0 };
            }
            acc[loc.warehouseName].items++;
            acc[loc.warehouseName].quantity += loc.quantity;
            acc[loc.warehouseName].value += loc.quantity * item.unitPrice;
          });
          return acc;
        }, {} as Record<string, { items: number; quantity: number; value: number }>);

      let response = "📍 **Stock Distribution by Warehouse:**\n\n";
      Object.entries(warehouseStats).forEach(([warehouse, stats]) => {
        response += `**${warehouse}**\n`;
        response += `• Items: ${stats.items}\n`;
        response += `• Units: ${stats.quantity.toLocaleString()}\n`;
        response += `• Value: $${stats.value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}\n\n`;
      });

      return response;
    }

    // SKU search
    if (lowerQuery.includes("search") || lowerQuery.includes("find") || lowerQuery.includes("sku") || lowerQuery.includes("code")) {
      // Extract potential SKU from query
      const words = query.split(/\s+/);
      const potentialSku = words.find(word => word.match(/^STK\d+$/i));
      
      if (potentialSku) {
        const item = stockItems.find(i => i.code.toLowerCase() === potentialSku.toLowerCase());
        if (item) {
          return `📦 **Product Details:**\n\n**${item.code}** - ${item.description}\n\n• **Category:** ${item.category}\n• **Quantity:** ${item.physicalQuantity} units\n• **Unit Price:** $${item.unitPrice.toFixed(2)}\n• **Total Value:** $${(item.physicalQuantity * item.unitPrice).toFixed(2)}\n• **Status:** ${item.inactive ? '🔴 Inactive' : '🟢 Active'}\n${item.barcode ? `• **Barcode:** ${item.barcode}` : ''}\n\n💡 You can view more details in the Stock Items page.`;
        }
        return `I couldn't find a stock item with code "${potentialSku}". Please check the code and try again.`;
      }

      return "To search for a specific item, please provide the stock code (e.g., STK001). You can also browse all items in the Stock Items page.";
    }

    // Recent movements (mock data)
    if (lowerQuery.includes("recent") && lowerQuery.includes("movement")) {
      return "📈 **Recent Stock Movements (Last 7 Days):**\n\n• **STK001** - Laptop Dell XPS 15\n  ↗️ Received: +20 units (Purchase Order #PO-2024-001)\n\n• **STK015** - Coffee Beans Premium\n  ↘️ Sold: -150 units (Sales Order #SO-2024-045)\n\n• **STK023** - Office Desk Standard\n  🔄 Transferred: 10 units (Warehouse A → Warehouse B)\n\n💡 View detailed movement history in the Stock Movements page.";
    }

    // Category inquiry
    if (lowerQuery.includes("category") || lowerQuery.includes("categories")) {
      const categories = [...new Set(stockItems.map(item => item.category))];
      const categoryStats = categories.map(cat => {
        const items = stockItems.filter(item => item.category === cat && !item.inactive);
        const totalQty = items.reduce((sum, item) => sum + item.physicalQuantity, 0);
        const totalValue = items.reduce((sum, item) => sum + (item.physicalQuantity * item.unitPrice), 0);
        return { category: cat, items: items.length, quantity: totalQty, value: totalValue };
      }).sort((a, b) => b.value - a.value);

      let response = "📂 **Stock by Category:**\n\n";
      categoryStats.forEach(stat => {
        response += `**${stat.category}**\n`;
        response += `• ${stat.items} items | ${stat.quantity} units | $${stat.value.toFixed(2)}\n\n`;
      });

      return response;
    }

    // Price inquiry
    if (lowerQuery.includes("price") && !lowerQuery.includes("high")) {
      return "To check pricing information:\n\n• For a specific item, provide the stock code (e.g., 'What is the price of STK001?')\n• For price analysis, try 'Show me high value items'\n• For category pricing, ask about a specific category\n\nWhat would you like to know?";
    }

    // Default response for unrecognized queries
    return "I'm not sure I understand that query. Here are some things I can help with:\n\n• **Stock Levels:** 'Show me low stock items'\n• **Inventory Value:** 'What is the total stock value?'\n• **Product Search:** 'Find stock item STK001'\n• **Warehouse Info:** 'Show warehouse distribution'\n• **Recent Activity:** 'What are recent stock movements?'\n• **Categories:** 'Show me stock by category'\n\nTry one of the quick action buttons below, or ask me a question!";
  };

  const handleSendMessage = async (content: string) => {
    if (!content.trim() || isProcessing) return;

    // Create user message
    const userMessage: Message = {
      id: crypto.randomUUID(),
      role: "user",
      content: content.trim(),
      timestamp: new Date(),
      status: "sending"
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue("");
    setIsProcessing(true);

    // Update user message status to sent
    setTimeout(() => {
      setMessages(prev => 
        prev.map(msg => 
          msg.id === userMessage.id ? { ...msg, status: "sent" } : msg
        )
      );
    }, 100);

    // Simulate AI processing delay
    setTimeout(() => {
      const response = processQuery(content);
      
      const assistantMessage: Message = {
        id: crypto.randomUUID(),
        role: "assistant",
        content: response,
        timestamp: new Date(),
        status: "sent"
      };

      setMessages(prev => [...prev, assistantMessage]);
      setIsProcessing(false);
    }, 800 + Math.random() * 700); // Random delay between 800-1500ms
  };

  const handleQuickAction = (action: QuickAction) => {
    handleSendMessage(action.query);
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage(inputValue);
    }
  };

  const clearConversation = () => {
    const welcomeMessage: Message = {
      id: crypto.randomUUID(),
      role: "assistant",
      content: "Conversation cleared. How can I assist you today?",
      timestamp: new Date(),
      status: "sent"
    };
    setMessages([welcomeMessage]);
    localStorage.removeItem("stock-assistant-messages");
  };

  return (
    <div className="flex flex-col h-full p-6 gap-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <Sparkles className="h-8 w-8 text-primary" />
            Stock Assistant
          </h1>
          <p className="text-muted-foreground mt-1">
            Your AI-powered inventory management assistant
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={clearConversation}>
          <Clock className="h-4 w-4 mr-2" />
          Clear History
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 flex-1 min-h-0">
        {/* Quick Actions Sidebar */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle className="text-lg">Quick Actions</CardTitle>
            <CardDescription>Common queries to get started</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {quickActions.map(action => (
              <Button
                key={action.id}
                variant="outline"
                className="w-full justify-start text-left h-auto py-3"
                onClick={() => handleQuickAction(action)}
                disabled={isProcessing}
              >
                <div className="flex items-start gap-3 w-full">
                  <div className="mt-0.5">{action.icon}</div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm">{action.label}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {action.category}
                    </div>
                  </div>
                </div>
              </Button>
            ))}
          </CardContent>
        </Card>

        {/* Chat Interface */}
        <Card className="lg:col-span-3 flex flex-col">
          <CardHeader>
            <CardTitle className="text-lg">Conversation</CardTitle>
            <CardDescription>
              Ask questions about your inventory in natural language
            </CardDescription>
          </CardHeader>
          <Separator />
          
          {/* Messages Area */}
          <CardContent className="flex-1 p-0 min-h-0">
            <ScrollArea className="h-full" ref={scrollAreaRef}>
              <div className="p-6 space-y-4">
                {messages.map((message) => (
                  <div
                    key={message.id}
                    className={cn(
                      "flex gap-3",
                      message.role === "user" ? "justify-end" : "justify-start"
                    )}
                  >
                    {message.role === "assistant" && (
                      <div className="flex-shrink-0">
                        <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                          <Bot className="h-5 w-5 text-primary" />
                        </div>
                      </div>
                    )}
                    
                    <div
                      className={cn(
                        "flex flex-col gap-1 max-w-[80%]",
                        message.role === "user" && "items-end"
                      )}
                    >
                      <div
                        className={cn(
                          "rounded-lg px-4 py-3",
                          message.role === "user"
                            ? "bg-primary text-primary-foreground"
                            : "bg-muted"
                        )}
                      >
                        <div className="text-sm whitespace-pre-wrap break-words">
                          {message.content}
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2 px-1">
                        <span className="text-xs text-muted-foreground">
                          {format(message.timestamp, "HH:mm")}
                        </span>
                        {message.role === "user" && message.status && (
                          <Badge
                            variant={
                              message.status === "failed"
                                ? "destructive"
                                : "secondary"
                            }
                            className="text-xs h-4 px-1"
                          >
                            {message.status === "sending" && "Sending..."}
                            {message.status === "sent" && "✓"}
                            {message.status === "failed" && "Failed"}
                          </Badge>
                        )}
                      </div>
                    </div>

                    {message.role === "user" && (
                      <div className="flex-shrink-0">
                        <div className="h-8 w-8 rounded-full bg-primary flex items-center justify-center">
                          <User className="h-5 w-5 text-primary-foreground" />
                        </div>
                      </div>
                    )}
                  </div>
                ))}

                {isProcessing && (
                  <div className="flex gap-3 justify-start">
                    <div className="flex-shrink-0">
                      <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                        <Bot className="h-5 w-5 text-primary" />
                      </div>
                    </div>
                    <div className="bg-muted rounded-lg px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span className="text-sm text-muted-foreground">
                          Processing your request...
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </ScrollArea>
          </CardContent>

          <Separator />

          {/* Input Area */}
          <CardContent className="p-4">
            <div className="flex gap-2">
              <Input
                placeholder="Ask about stock levels, movements, pricing, or anything else..."
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={isProcessing}
                className="flex-1"
              />
              <Button
                onClick={() => handleSendMessage(inputValue)}
                disabled={!inputValue.trim() || isProcessing}
                size="icon"
              >
                {isProcessing ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Press Enter to send • Conversation is saved automatically
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
